import { updatePageProps, track } from '@eventbrite/legacy-infra.datalayer-library';
import { LISTING_HEAP_IDS } from '@/components/_shared/constants';
import type { WebappResponse } from '@eventbrite/event-listing-service-client';
import { getRefundPolicy } from './utils';
import type { TicketInformationResponse } from '@/pages/api/[eventId]/ticket-information.page';
import { getHighLevelStatus, HighLevelStatus } from './highLevelStatus';
export interface CorePageProperties {
  listingSource: string;
  timedEntry: boolean;

  eventId: string;
  eventStartDate: string;
  eventEndDate: string;
  eventIsEnded: boolean;
  eventOrganizerId: string;
  eventIsOnlineEvent: boolean;
  eventOwnerId: string;
  eventCountry?: string;
  eventRegion?: string;
  eventStatus: string;
  eventStatusSales: string | undefined;
  eventIsCanceled?: boolean;
  eventIsPostponed?: boolean;
  eventIsSalesEnded?: boolean;
  eventIsSoldOut?: boolean;

  isLive: boolean;
  isOnline: boolean;
  isPrivate: boolean;
  isRepeatingEvent: boolean;

  multiticket: boolean;
  hasHeroImages: boolean;
  hasImages: boolean;

  childEventId?: string;

  isFree: boolean;
  isPaid: boolean;
  isSeriesParent: boolean;
  isListed: boolean;
  locale: string;

  eventCurrency: string;
  eventSource: string;
  eventIsExpired: boolean;

  // Event taxonomies
  category: string;
  subcategory: string;
  format: string;
  eventCategory: string;
  eventSubcategory: string;
  eventFormat: string;

  // Lineup
  hasArtistWidget: boolean;

  // Related to "Good to know"
  goodToKnowAge: boolean;
  goodToKnowCheckin: boolean;
  goodToKnowParking?: string;
  goodToKnowFAQNumber: number;
  eventRefund: string;
  eventAgeRestriction: string;
  eventDurationSeconds?: number;

  // RSVP
  hasRSVP: boolean;

  // External tickets
  hasExternalTickets: boolean;

  // Waitlist (set client-side when ticket info is available)
  hasWaitlistEnabled?: boolean;

  // Authentication (set client-side when user info is available)
  isAuthenticated: boolean;

  // Referrer source (true when traffic comes from an Eventbrite domain)
  EBSource: boolean;

  // Ticket information
  eventHasReservedSeating?: boolean;
  eventHasMoreThanOneTicket?: boolean;
  eventHasDiscounts?: boolean;
  eventHasDonations?: boolean;
  eventHasAddons?: boolean;

  // FAQs / content (context-derived)
  faqsNumber?: number;
  hasFaqs?: boolean;
  hasAgenda?: boolean;
  hasDescriptionImages?: boolean;

  // Hero / video (context-derived)
  hasVideo?: boolean;
  heroVideo?: boolean;
  videoSource?: 'service' | 'url' | null; // "service" if it is a native video, "url" if it is a featured video

  // Discounts / tickets (client-side after fetch)
  has2x1?: boolean;
  hasEarlyBirdDiscount?: boolean;
  hasOpenDiscount?: boolean;
  hasPrivateDiscount?: boolean;

  // Urgency signals (client-side after fetch)
  hasUrgencySignals?: boolean;
  hasFewTicketsLeftUrgencySignal?: boolean;
  hasGoingFastUrgencySignal?: boolean;
  hasNewUrgencySignal?: boolean;
  hasPopularUrgencySignal?: boolean;
  hasSalesEndSoonUrgencySignal?: boolean;
}

/**
 *  Ongoing work to add all page properties. This interface should eventually be removed as we
 *  complete adding all the needed properties.
 *
 *  For reference, the legacy page properties are defined in:
 *  - https://github.com/eventbrite/eb-ui/blob/main/packages/attendee/listings/src/tracking/dataLayerMiddleware.ts
 *  - https://github.com/eventbrite/analytics-instrumentation/blob/main/google-tag-manager/tag_generators/heap_page_props/src/reducers/event-listing.ts
 */
interface NotYetAvailablePageProperties {
  hasAutogeneratedContent: boolean; // TODO: Add this prop to the context within structured content block

  // Unclassified
  isSeriesInfiniteCalendar: boolean; // Deprecated?
  shouldShowExpressCheckout: boolean; // Deprecated?
  eventShouldShowCompactCheckout: boolean; // Deprecated?
}

type PageProperties = CorePageProperties & Partial<NotYetAvailablePageProperties>;

export function addPagePropertiesFromRenderContext(context: WebappResponse, isAuthenticated: boolean) {
  const isEventEnded = ['ended', 'completed', 'deleted'].includes(context.basicInfo.status);
  const isFree = context.basicInfo.isFree || false;
  const hasFeaturedVideo = !!context.gallery?.featuredVideo?.url;
  const hasNativeVideo = !!context.gallery?.nativeVideo?.url;
  const hasVideoInDescription = !!context.structuredContent?.modules?.some(
    (m: { type?: string }) => m.type === 'video',
  );

  const hasVideo = hasVideoInDescription || hasFeaturedVideo || hasNativeVideo;

  const page: CorePageProperties = {
    listingSource: 'web:valkyrie',
    timedEntry: context.basicInfo.isTimedEntry ?? false,
    eventEndDate: context.basicInfo.endDate.utc || '',
    eventId:
      context.basicInfo.isSeries && context.basicInfo.seriesId ? context.basicInfo.seriesId : context.basicInfo.id,
    eventStartDate: context.basicInfo.startDate.utc || '',
    hasHeroImages: Array.isArray(context.gallery?.images) && context.gallery.images.length > 0,
    hasImages: Array.isArray(context.gallery?.images) && context.gallery.images.length > 0,
    isLive: context.basicInfo.status === 'live',
    isOnline: context.basicInfo.isOnline || false,
    isPrivate: context.basicInfo.isProtected || false,
    isRepeatingEvent: context.basicInfo.isSeries || false,
    multiticket: false,
    eventCountry: context.basicInfo.venue?.address?.country,
    eventIsEnded: isEventEnded,
    eventIsExpired: isEventEnded,
    eventIsOnlineEvent: context.basicInfo.isOnline || false,
    eventOwnerId: context.basicInfo.organizationId,
    eventRegion: context.basicInfo.venue?.address?.region,
    eventOrganizerId: context.basicInfo.organizer.id,
    childEventId: context.basicInfo.isSeries ? context.basicInfo.id : undefined,
    isFree,
    isPaid: !isFree,
    isSeriesParent: context.basicInfo.isParentEvent,
    isListed: context.basicInfo.isListed,
    locale: context.basicInfo.locale,
    eventStatus: context.basicInfo.status,
    eventStatusSales: context.salesStatus?.salesStatus,
    ...getSalesStateFlags(context.salesStatus),
    eventCurrency: context.basicInfo.currency,
    eventSource: context.basicInfo.ticketsBy,
    category: normalizeValue(context.taxonomies?.category),
    subcategory: normalizeValue(context.taxonomies?.subcategory),
    format: normalizeValue(context.taxonomies?.format),
    eventCategory: normalizeValue(context.taxonomies?.category),
    eventSubcategory: normalizeValue(context.taxonomies?.subcategory),
    eventFormat: normalizeValue(context.taxonomies?.format),
    hasArtistWidget: typeof context.artistInfo?.artistType !== 'undefined' || false,
    goodToKnowAge: !!context.goodToKnow?.highlights?.ageRestriction,
    goodToKnowParking: context.goodToKnow?.highlights?.parking,
    goodToKnowCheckin: !!context.goodToKnow?.highlights?.doorTime,
    goodToKnowFAQNumber: context.structuredContent?.widgets?.faqs?.length || 0,
    eventRefund: getRefundPolicy(context.goodToKnow?.refundPolicy),
    eventAgeRestriction: context.goodToKnow?.highlights?.ageRestriction || '',
    hasRSVP: getHasRSVPFromUrl(),
    hasExternalTickets: false, // TODO: Implement this when opening to external tickets
    isAuthenticated: isAuthenticated,
    eventHasReservedSeating: context.basicInfo.eventHasReservedSeating ?? false,
    eventDurationSeconds: context.basicInfo.eventDurationSeconds,

    hasVideo,
    heroVideo: hasFeaturedVideo || hasNativeVideo,
    videoSource: getVideoSource(hasFeaturedVideo, hasNativeVideo),

    hasAgenda: !!context.structuredContent?.widgets?.agenda,
    hasFaqs: !!context.structuredContent?.widgets?.faqs?.length,
    faqsNumber: context.structuredContent?.widgets?.faqs?.length || 0,

    hasDescriptionImages: !!context.structuredContent?.modules?.some((m: { type?: string }) => m.type === 'image'),

    EBSource: getEBSource(),
  };

  updatePageProperties(page);
}

function updatePageProperties(page: PageProperties) {
  updatePageProps({ page });
}

// ------------------------------------------------------------
// Render Context Helpers
// ------------------------------------------------------------

function normalizeValue(value: string | undefined): string {
  return value?.toLocaleLowerCase().replace(/\s/g, '_') || '';
}

function getSalesStateFlags(salesStatusData?: WebappResponse['salesStatus']) {
  const status = getHighLevelStatus(salesStatusData);
  return {
    eventIsCanceled: status === HighLevelStatus.CANCELLED,
    eventIsPostponed: status === HighLevelStatus.POSTPONED,
    eventIsSalesEnded: status === HighLevelStatus.SALES_ENDED,
    eventIsSoldOut: status === HighLevelStatus.SOLD_OUT,
  };
}

function getVideoSource(hasFeaturedVideo: boolean, hasNativeVideo: boolean): 'service' | 'url' | null {
  if (hasNativeVideo) return 'service';
  if (hasFeaturedVideo) return 'url';
  return null;
}

function getHasRSVPFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return Array.from(params.keys()).some((k) => ['mipa'].includes(k.toLowerCase()));
}

function getEBSource(): boolean {
  if (typeof document === 'undefined' || document.referrer === '') return false;
  try {
    const referrer = new URL(document.referrer);
    return referrer.hostname.includes('eventbrite');
  } catch {
    return false;
  }
}

// ------------------------------------------------------------
// Client-side Helpers
// ------------------------------------------------------------

export function updateWaitlistPageProperty(hasWaitlistEnabled: boolean) {
  updatePageProps({ page: { hasWaitlistEnabled } });
}

export function updateTicketInformationPageProperties(ticketInformation: TicketInformationResponse) {
  function hasDiscountType(discountType: 'coded' | 'access' | 'public' | 'hold' | 'open') {
    return ticketInformation?.discounts?.some((d) => d.discount_type === discountType);
  }

  const has2x1 = ticketInformation?.availability?.hasBogoTickets ?? false;
  const hasEarlyBirdDiscount = ticketInformation?.availability?.hasEarlyBirdTickets ?? false;
  const hasOpenDiscount = hasDiscountType('open');
  const hasPrivateDiscount = hasDiscountType('coded');

  updatePageProps({
    page: {
      eventHasMoreThanOneTicket: (ticketInformation?.numTicketClasses ?? 0) > 1 || false,
      eventHasAddons: ticketInformation?.eventHasAddons ?? false,
      eventHasDonations: ticketInformation?.eventHasDonations ?? false,
      eventTotalTicketsCount: ticketInformation?.numTicketClasses ?? 0,
      has2x1,
      hasEarlyBirdDiscount,
      hasOpenDiscount,
      hasPrivateDiscount,
      eventHasDiscounts: has2x1 || hasEarlyBirdDiscount || hasOpenDiscount || hasPrivateDiscount,
    },
  });
}

export function updateUrgencySignalsPageProperties(signals: string[]) {
  updatePageProps({ page: urgencySignalsToPageProperties(signals) });
}

/**
 * Maps urgency-signals API response (array of signal names) to HEAP page properties.
 */
export function urgencySignalsToPageProperties(signals: string[]) {
  const set = new Set(signals);
  return {
    hasUrgencySignals: signals.length > 0,
    hasEarlyBirdDiscount: set.has('earlyBird'),
    hasFewTicketsLeftUrgencySignal: set.has('fewTickets'),
    hasGoingFastUrgencySignal: set.has('goingFast'),
    hasNewUrgencySignal: set.has('new'),
    hasPopularUrgencySignal: set.has('popular'),
    hasSalesEndSoonUrgencySignal: set.has('salesEndSoon'),
  };
}

/**
 * Fires a custom event after all lazy-loaded page properties (e.g. hasWaitlistEnabled)
 * have been pushed to the dataLayer. GTM picks up this event and can re-send the
 * now-complete page properties to Heap, ensuring they are available for analytics
 * even though they were not present at initial page load.
 */
export function trackConversionBarHydration() {
  track({ eventName: LISTING_HEAP_IDS.CONVERSION_BAR_HYDRATE, eventData: {} });
}
